import React, { useState, useEffect, useRef } from "react";
import { Upload, PencilLine, FileText, CheckCircle2, X, Trash2, Search, Download } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money } from "../lib/api";
import SearchableSelect from "../components/ui/SearchableSelect";
import useAutoRefresh from "../lib/useAutoRefresh";
import { exportPurchaseInvoicesToCsv } from "../lib/exportUtils";

export default function PurchaseInvoices({ skus, onInventoryChanged, onManualEntry }) {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const inputRef = useRef(null);

  const fetchInvoices = async () => {
    try {
      const r = await api.get("/inventory/purchase-invoices?limit=100");
      setInvoices(r.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Purchase orders are raised in other tabs and by other users — keep the
  // list current without asking anyone to press a refresh button.
  useAutoRefresh(fetchInvoices, 30000);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/inventory/purchase-invoices/${id}`);
      setDetail(r.data);
      setEditingPI(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const [editingPI, setEditingPI] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const startEditPI = () => {
    if (!detail) return;
    setEditForm({
      invoice_number: detail.invoice_number || "",
      vendor: detail.vendor || "",
      invoice_date: (detail.invoice_date || "").slice(0, 10),
      // Total GST in rupees (source of truth), prefilled from the invoice.
      gst_amount: detail.gst_total ?? 0,
      lines: (detail.lines || []).map((l) => ({
        line_id: l.line_id || null, sku_id: l.sku_id || null,
        sku_name: l.sku_name || "", product_name: l.sku_name || "",
        quantity: l.quantity ?? 0, unit_cost: l.unit_cost ?? 0,
      })),
    });
    setEditingPI(true);
  };

  const saveEditPI = async () => {
    if (!detail) return;
    if (!editForm.invoice_number.trim()) return toast.error("Invoice number is required");
    setEditSaving(true);
    try {
      const res = await api.put(`/inventory/purchase-invoices/${detail.id}`, {
        ...editForm,
        // Coerce to a number — blank means "no GST" (0), not a parse error.
        gst_amount: Number(editForm.gst_amount) || 0,
      });
      toast.success(`Invoice saved · ${money(res.data.total)}`);
      setEditingPI(false);
      setEditForm(null);
      const r = await api.get(`/inventory/purchase-invoices/${detail.id}`);
      setDetail(r.data);
      fetchInvoices();
      onInventoryChanged && onInventoryChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setEditSaving(false);
    }
  };

  const handlePdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/inventory/purchase-invoice/parse-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setDraft({ ...r.data, fileName: f.name });
      setDetail(null);
      if ((r.data.lines || []).length === 0) {
        toast.warning("PDF read but no item rows found — check the file and add rows manually.");
      } else {
        toast.success(`Read ${r.data.lines.length} rows (${r.data.matched_count || 0} auto-matched). Verify, then confirm.`);
        const w = (r.data.warnings || []).length;
        if (w > 0) {
          toast.warning(`${w} row${w > 1 ? "s" : ""} were auto-corrected or guessed — check the amber flags before confirming.`);
        }
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setLine = (idx, patch) => {
    const lines = [...(draft.lines || [])];
    lines[idx] = { ...lines[idx], ...patch };
    if (patch.sku_id !== undefined) {
      const s = (skus || []).find((x) => x.id === patch.sku_id);
      lines[idx].sku_name = s?.name || "";
      lines[idx].matched = !!s;
    }
    setDraft({ ...draft, lines });
  };

  const removeLine = (idx) => {
    setDraft({ ...draft, lines: draft.lines.filter((_, i) => i !== idx) });
  };

  const draftTotal = ((draft && draft.lines) || []).reduce((t, l) => t + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);
  const readyLines = ((draft && draft.lines) || []).filter((l) => l.sku_id && Number(l.quantity) > 0);
  // Every row with a quantity is saved under the invoice number -- mapped
  // rows move stock, the rest are kept unmapped for future use.
  const savableLines = ((draft && draft.lines) || []).filter((l) => Number(l.quantity) > 0);
  // Rows the parser had to correct or could not resolve — every one of these
  // must be eyeballed before dumping to inventory.
  const flagged = ((draft && draft.lines) || []).filter((l) => l.warning || !l.sku_id).length;

  const handleConfirm = async () => {
    if (!draft.invoice_number?.trim()) return toast.error("Invoice number is required");
    if (savableLines.length === 0) return toast.error("Add at least one row with quantity");
    setConfirmBusy(true);
    try {
      const res = await api.post("/inventory/purchase-invoice/confirm-draft", {
        invoice_number: draft.invoice_number.trim(),
        vendor: draft.vendor || "",
        invoice_date: (draft.invoice_date || "").slice(0, 10),
        notes: `PDF import: ${draft.fileName || ""}`,
        lines: savableLines.map((l) => ({
          sku_id: l.sku_id || null,
          product_name: l.product_name || "",
          quantity: Number(l.quantity) || 0,
          unit_cost: Number(l.unit_cost) || 0,
          batch_number: l.batch_number || "",
          expiry_date: l.expiry_date || null,
        })),
      });
      const kept = res.data.lines_saved_unmapped || 0;
      toast.success(`Saved under ${draft.invoice_number.trim()}: ${res.data.lines_imported} stocked${kept ? `, ${kept} kept unmapped for later` : ""} · ${money(res.data.total)}`);
      setDraft(null);
      fetchInvoices();
      onInventoryChanged && onInventoryChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? invoices.filter((p) =>
        (p.invoice_number || "").toLowerCase().includes(q) ||
        (p.vendor || "").toLowerCase().includes(q)
      )
    : invoices;

  return (
    <div className="lss-card p-6 space-y-6">
      <div>
        <span className="lss-overline text-amber-800">Purchase Inward</span>
        <h2 className="font-serif-lux text-2xl font-bold text-slate-950 mt-1">Purchase Invoices</h2>
        <p className="text-slate-600 text-sm font-medium mt-1">
          Option 1: upload vendor PDF → verify rows → dump to inventory. Option 2: manual entry.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="lss-card p-6 bg-slate-50/60 space-y-4">
          <h3 className="font-bold text-slate-950 text-lg">Option 1 — Upload Vendor Bill (PDF / Photo / Excel)</h3>
          <p className="text-xs text-slate-600 font-medium">
            Reads invoice no, vendor, date and item rows from PDFs, scanned PDFs, or bill photos. Nothing touches inventory until you verify and confirm.
          </p>
          <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.bmp,.tif,.tiff,.xlsx,.xls,.csv" onChange={handlePdf} className="hidden" />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="lss-btn-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> {busy ? "Reading PDF..." : "Choose PDF Invoice"}
          </button>
        </div>
        <div className="lss-card p-6 bg-slate-50/60 space-y-4">
          <h3 className="font-bold text-slate-950 text-lg">Option 2 — Manual Entry</h3>
          <p className="text-xs text-slate-600 font-medium">
            Enter invoice header and line items by hand for non-PDF bills.
          </p>
          <button
            onClick={() => onManualEntry && onManualEntry()}
            className="lss-btn-outline px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2"
          >
            <PencilLine className="w-4 h-4" /> Open Manual Entry
          </button>
        </div>
      </div>

      {draft && (
        <div className="border border-amber-300 rounded-2xl overflow-hidden">
          <div className="bg-slate-950 text-white px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="lss-overline text-amber-300">Verify before dump {draft.fileName ? `· ${draft.fileName}` : ""}</div>
              <div className="font-extrabold text-sm mt-0.5">
                {readyLines.length}/{draft.lines.length} rows ready · {money(draftTotal)}
                {flagged > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-[10px] font-extrabold uppercase rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40 align-middle">
                    {flagged} flagged
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => setDraft(null)}
                className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl border border-slate-600 text-slate-300 hover:text-white transition"
              >
                Discard
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirmBusy || savableLines.length === 0}
                className="px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl bg-amber-400 text-slate-950 hover:bg-amber-300 transition disabled:opacity-50"
              >
                {confirmBusy ? "Saving..." : `Save Bill${readyLines.length ? ` (${readyLines.length} to stock)` : ""}`}
              </button>
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-amber-50/50 border-b border-amber-200">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Invoice # *</label>
              <input value={draft.invoice_number || ""} onChange={(e) => setDraft({ ...draft, invoice_number: e.target.value })} className="lss-input w-full text-xs font-bold" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Vendor</label>
              <input value={draft.vendor || ""} onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} className="lss-input w-full text-xs font-semibold" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Date</label>
              <input type="date" value={(draft.invoice_date || "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, invoice_date: e.target.value })} className="lss-input w-full text-xs font-semibold" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="lss-table">
              <thead>
                <tr className="bg-slate-950 text-amber-300">
                  <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase">PDF Item</th>
                  <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase">Master SKU *</th>
                  <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase">Qty</th>
                  <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase">Unit Cost</th>
                  <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase">Amount</th>
                  <th className="text-center py-2.5 px-3 text-[10px] font-extrabold uppercase">Drop</th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((l, idx) => (
                  <tr key={idx} className={`hover:bg-slate-50/80 ${!l.sku_id ? "bg-purple-50/60" : ""} ${l.warning ? "!bg-amber-50/70" : ""}`}>
                    <td className="text-xs font-semibold text-slate-800">
                      {l.product_name}
                      {!l.sku_id && <span className="ml-2 text-[10px] font-extrabold uppercase text-purple-800 bg-purple-100 border border-purple-300 rounded px-1.5 py-0.5">pick SKU</span>}
                      {l.warning && (
                        <div className="mt-1 text-[10px] font-bold leading-snug text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-1 max-w-[260px]">
                          {l.warning}
                        </div>
                      )}
                    </td>
                    <td className="min-w-[220px]">
                      <SearchableSelect
                        options={skus || []}
                        value={l.sku_id || ""}
                        onChange={(val) => setLine(idx, { sku_id: val })}
                        placeholder="Link Master SKU..."
                        getOptionLabel={(s) => `${s.name} — ${s.vendor_name || "No vendor"}`}
                        getOptionValue={(s) => s.id}
                      />
                    </td>
                    <td>
                      <input type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(idx, { quantity: parseFloat(e.target.value) || 0 })} className="lss-input w-24 text-xs font-bold text-right tabular" />
                    </td>
                    <td>
                      <input type="number" min="0" step="any" value={l.unit_cost} onChange={(e) => setLine(idx, { unit_cost: parseFloat(e.target.value) || 0 })} className="lss-input w-28 text-xs font-bold text-right tabular" />
                    </td>
                    <td className="text-right tabular font-bold text-xs text-slate-900">{money((Number(l.quantity) || 0) * (Number(l.unit_cost) || 0))}</td>
                    <td className="text-center">
                      <button onClick={() => removeLine(idx)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg" title="Drop row">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-slate-950 text-lg">All Purchase Invoices ({visible.length})</h3>
          <p className="text-xs text-slate-500 font-medium">
            Click any row to view or edit line items, rates, and GST split
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice # or vendor…"
              className="lss-input lss-search w-full text-xs font-semibold pl-9"
            />
          </div>
          <button
            onClick={() =>
              exportPurchaseInvoicesToCsv(
                visible,
                `Purchase_Invoices_${new Date().toISOString().slice(0, 10)}.csv`,
                search ? `Filtered: "${search}" · ${visible.length} invoices` : `All ${visible.length} invoices`
              )
            }
            disabled={visible.length === 0}
            className="bg-slate-950 hover:bg-slate-800 text-white px-3.5 py-2 text-xs font-extrabold rounded-xl flex items-center gap-1.5 transition shrink-0 disabled:opacity-50 shadow-xs"
            title="Export entered purchase invoices with itemized line items, rates and GST to CSV"
          >
            <Download className="w-4 h-4 text-amber-300" />
            <span>Export Invoices (CSV)</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-xl">
        <table className="lss-table">
          <thead>
            <tr className="bg-slate-950 text-amber-300">
              <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase">Invoice #</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase">Vendor</th>
              <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase">Date</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase">Lines</th>
              <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase">Total</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.id} onClick={() => openDetail(p.id)} className="hover:bg-amber-50/60 cursor-pointer transition-colors">
                <td className="font-mono font-bold text-xs text-slate-900">{p.invoice_number}</td>
                <td className="text-xs font-semibold text-slate-800">{p.vendor || "—"}</td>
                <td className="text-xs text-slate-600">{p.invoice_date || "—"}</td>
                <td className="text-right tabular text-xs font-bold">{(p.lines || []).length}</td>
                <td className="text-right tabular font-extrabold text-xs text-emerald-800">{money(p.total)}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm font-medium">No purchase invoices yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-950 text-white px-6 py-4 flex items-start justify-between">
              <div>
                <div className="lss-overline text-amber-300">Purchase Invoice Detail</div>
                {editingPI ? (
                  <input value={editForm.invoice_number} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} className="mt-1 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 font-serif-lux text-xl font-bold w-full" />
                ) : (
                  <h3 className="font-serif-lux text-xl font-bold mt-0.5">{detail.invoice_number}</h3>
                )}
                {editingPI ? (
                  <div className="flex gap-2 mt-2">
                    <input value={editForm.vendor} onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })} placeholder="Vendor" className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs font-medium w-40" />
                    <input type="date" value={editForm.invoice_date} onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })} className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs font-medium" />
                  </div>
                ) : (
                  <p className="text-xs text-slate-300 font-medium mt-0.5">{detail.vendor || "—"} · {detail.invoice_date || "—"} · by {detail.created_by || "—"}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!editingPI ? (
                  <>
                    <button
                      onClick={() =>
                        exportPurchaseInvoicesToCsv(
                          [detail],
                          `Purchase_Invoice_${(detail.invoice_number || detail.id).replace(/[^\w-]/g, "_")}.csv`,
                          `Invoice: ${detail.invoice_number || detail.id} · ${detail.vendor || ""}`
                        )
                      }
                      title="Export this purchase invoice to CSV"
                      className="p-2 text-slate-300 hover:text-white flex items-center gap-1 text-xs font-bold uppercase transition"
                    >
                      <Download className="w-4 h-4 text-amber-300" /> Export CSV
                    </button>
                    <button onClick={startEditPI} title="Edit invoice" className="p-2 text-amber-300 hover:text-white flex items-center gap-1 text-xs font-bold uppercase">
                      <PencilLine className="w-4 h-4" /> Edit
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditingPI(false)} className="p-2 text-slate-400 hover:text-white text-xs font-bold uppercase">Cancel</button>
                    <button onClick={saveEditPI} disabled={editSaving} className="p-2 text-emerald-300 hover:text-white text-xs font-bold uppercase disabled:opacity-50">{editSaving ? "Saving…" : "Save"}</button>
                  </>
                )}
                <button onClick={() => { setDetail(null); setEditingPI(false); }} className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="lss-table">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="text-left py-2 px-3 text-[10px] font-extrabold uppercase">Product</th>
                    <th className="text-right py-2 px-3 text-[10px] font-extrabold uppercase">Qty</th>
                    <th className="text-right py-2 px-3 text-[10px] font-extrabold uppercase">Unit Cost</th>
                    <th className="text-right py-2 px-3 text-[10px] font-extrabold uppercase">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(editingPI && editForm ? editForm.lines.map((ef, i) => ({
                    ef,
                    label: ef._new ? "" : (ef.sku_name || ef.product_name || ""),
                  })) : (detail.lines || []).map((l, i) => ({ ef: null, label: l.sku_name }))).map(({ ef, label }, i) => {
                    const isNew = !!(ef && ef._new);
                    // A bill row saved without a product can still be linked to
                    // one here -- picking a product moves that quantity into
                    // stock, exactly as the original receipt did.
                    const needsLink = !!(ef && !ef.sku_id);
                    return (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="text-xs font-bold text-slate-900">
                        {(isNew || needsLink) ? (
                          <SearchableSelect
                            options={skus || []}
                            value={ef.sku_id || ""}
                            onChange={(val) => {
                              const s = (skus || []).find((x) => x.id === val);
                              setEditForm({ ...editForm, lines: editForm.lines.map((x, j) => j === i ? { ...x, sku_id: val, product_name: s?.name || "", sku_name: s?.name || "", ...(isNew ? { unit_cost: s?.unit_cost || 0 } : {}) } : x) });
                            }}
                            placeholder="Pick product..."
                            getOptionLabel={(s) => `${s.name} — ${s.vendor_name || "No vendor"}`}
                            getOptionValue={(s) => s.id}
                          />
                        ) : label}
                        {needsLink && !isNew && (
                          <span className="ml-2 text-[10px] font-extrabold uppercase text-purple-800 bg-purple-100 border border-purple-300 rounded px-1.5 py-0.5">not linked</span>
                        )}
                        {isNew && (
                          <button onClick={() => setEditForm({ ...editForm, lines: editForm.lines.filter((_, j) => j !== i) })} className="ml-2 text-[10px] font-bold text-rose-600 hover:text-rose-900 underline" title="Drop new row">drop</button>
                        )}
                      </td>
                      <td className="text-right tabular text-xs">
                        {ef ? (
                          <input type="number" min="0" step="any" value={ef.quantity}
                            onChange={(e) => setEditForm({ ...editForm, lines: editForm.lines.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x) })}
                            className="lss-input w-20 text-xs text-right tabular" />
                        ) : (detail.lines || [])[i]?.quantity}
                      </td>
                      <td className="text-right tabular text-xs">
                        {ef ? (
                          <input type="number" min="0" step="any" value={ef.unit_cost}
                            onChange={(e) => setEditForm({ ...editForm, lines: editForm.lines.map((x, j) => j === i ? { ...x, unit_cost: parseFloat(e.target.value) || 0 } : x) })}
                            className="lss-input w-24 text-xs text-right tabular" />
                        ) : money((detail.lines || [])[i]?.unit_cost)}
                      </td>
                      <td className="text-right tabular text-xs font-bold">
                        {ef ? money((Number(ef.quantity) || 0) * (Number(ef.unit_cost) || 0)) : money((detail.lines || [])[i]?.line_total)}
                      </td>
                    </tr>
                    );
                  })}
                  {editingPI && (
                    <tr>
                      <td colSpan={4} className="py-2">
                        <button
                          onClick={() => setEditForm({ ...editForm, lines: [...editForm.lines, { _new: true, line_id: null, sku_id: "", product_name: "", quantity: 1, unit_cost: 0 }] })}
                          className="lss-btn-outline px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
                        >
                          + Add Item
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-slate-200">
              {editingPI ? (
                (() => {
                  const subtotal = (editForm?.lines || []).reduce(
                    (t, l) => t + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);
                  const gst = Number(editForm?.gst_amount) || 0;
                  return (
                    <div className="flex justify-end">
                      <div className="w-full sm:w-80 space-y-2">
                        <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                          <span>Subtotal (ex-GST)</span>
                          <span className="tabular">{money(subtotal)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                          <label htmlFor="edit-gst-amount" className="font-bold uppercase tracking-wide">Total GST (₹)</label>
                          <input
                            id="edit-gst-amount"
                            type="number"
                            min="0"
                            step="any"
                            value={editForm?.gst_amount ?? ""}
                            onChange={(e) => setEditForm({ ...editForm, gst_amount: e.target.value })}
                            placeholder="0"
                            title="Total GST in rupees printed on the vendor bill"
                            className="lss-input w-28 text-xs font-bold tabular text-right"
                          />
                        </div>
                        <div className="flex justify-between text-sm font-extrabold text-slate-950 border-t border-slate-200 pt-1.5 mt-1">
                          <span>Invoice Total</span>
                          <span className="tabular">{money(subtotal + gst)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex justify-end">
                  <span className="text-sm font-extrabold text-slate-950">Total: {money(detail.total)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
